
import React, { useState, useEffect } from 'react';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Slider } from '@/components/ui/slider';
import { Input } from '@/components/ui/input';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';

interface Goal {
  id: string;
  label: string;
  priority?: number;
  selected: boolean;
  custom?: boolean;
  customValue?: string;
}

interface ProfileFinancialGoalsProps {
  data: any;
  updateData: (data: any) => void;
}

const ProfileFinancialGoals = ({ data, updateData }: ProfileFinancialGoalsProps) => {
  const initialShortTermGoals: Goal[] = data.shortTermGoals || [
    { id: 'emergency-fund', label: 'Emergency Fund', selected: false, priority: 0 },
    { id: 'travel', label: 'Travel', selected: false, priority: 0 },
    { id: 'vehicle', label: 'Vehicle Purchase', selected: false, priority: 0 },
    { id: 'wedding', label: 'Wedding Expenses', selected: false, priority: 0 },
    { id: 'home-renovation', label: 'Home Renovation', selected: false, priority: 0 },
    { id: 'other', label: 'Other', selected: false, priority: 0, custom: true, customValue: '' }
  ];
  
  const initialMediumTermGoals: Goal[] = data.mediumTermGoals || [
    { id: 'house-down-payment', label: 'House Down Payment', selected: false, priority: 0 },
    { id: 'children-education', label: "Children's Education", selected: false, priority: 0 },
    { id: 'business-startup', label: 'Business Startup', selected: false, priority: 0 },
    { id: 'debt-repayment', label: 'Debt Repayment', selected: false, priority: 0 },
    { id: 'other', label: 'Other', selected: false, priority: 0, custom: true, customValue: '' }
  ];
  
  const initialLongTermGoals: Goal[] = data.longTermGoals || [
    { id: 'retirement', label: 'Retirement', selected: false, priority: 0 },
    { id: 'children-higher-education', label: "Children's Higher Education/Marriage", selected: false, priority: 0 },
    { id: 'financial-independence', label: 'Financial Independence', selected: false, priority: 0 },
    { id: 'legacy-planning', label: 'Legacy Planning', selected: false, priority: 0 },
    { id: 'other', label: 'Other', selected: false, priority: 0, custom: true, customValue: '' }
  ];
  
  const [shortTermGoals, setShortTermGoals] = useState<Goal[]>(initialShortTermGoals);
  const [mediumTermGoals, setMediumTermGoals] = useState<Goal[]>(initialMediumTermGoals);
  const [longTermGoals, setLongTermGoals] = useState<Goal[]>(initialLongTermGoals);
  
  useEffect(() => {
    updateData({
      shortTermGoals,
      mediumTermGoals,
      longTermGoals
    });
  }, [shortTermGoals, mediumTermGoals, longTermGoals, updateData]);
  
  const toggleGoalSelection = (goalType: 'short' | 'medium' | 'long', goalId: string) => {
    if (goalType === 'short') {
      setShortTermGoals(prev => prev.map(goal => 
        goal.id === goalId ? { ...goal, selected: !goal.selected } : goal
      ));
    } else if (goalType === 'medium') {
      setMediumTermGoals(prev => prev.map(goal => 
        goal.id === goalId ? { ...goal, selected: !goal.selected } : goal
      ));
    } else {
      setLongTermGoals(prev => prev.map(goal => 
        goal.id === goalId ? { ...goal, selected: !goal.selected } : goal
      ));
    }
  };
  
  const updateGoalPriority = (goalType: 'short' | 'medium' | 'long', goalId: string, priority: number) => {
    if (goalType === 'short') {
      setShortTermGoals(prev => prev.map(goal => 
        goal.id === goalId ? { ...goal, priority } : goal
      ));
    } else if (goalType === 'medium') {
      setMediumTermGoals(prev => prev.map(goal => 
        goal.id === goalId ? { ...goal, priority } : goal
      ));
    } else {
      setLongTermGoals(prev => prev.map(goal => 
        goal.id === goalId ? { ...goal, priority } : goal
      ));
    }
  };
  
  const updateCustomGoalValue = (goalType: 'short' | 'medium' | 'long', value: string) => {
    if (goalType === 'short') {
      setShortTermGoals(prev => prev.map(goal => 
        goal.id === 'other' ? { ...goal, customValue: value } : goal
      ));
    } else if (goalType === 'medium') {
      setMediumTermGoals(prev => prev.map(goal => 
        goal.id === 'other' ? { ...goal, customValue: value } : goal
      ));
    } else {
      setLongTermGoals(prev => prev.map(goal => 
        goal.id === 'other' ? { ...goal, customValue: value } : goal
      ));
    }
  };
  
  const renderGoalsList = (
    goals: Goal[], 
    goalType: 'short' | 'medium' | 'long', 
    title: string, 
    description: string
  ) => (
    <div className="mb-8">
      <h3 className="text-lg font-medium mb-2">{title}</h3>
      <p className="text-white/70 text-sm mb-4">{description}</p>
      
      <div className="space-y-4">
        {goals.map(goal => (
          <div key={goal.id} className="rounded-md border border-[#9E7C47]/20 p-3">
            <div className="flex items-center">
              <Checkbox
                id={`${goalType}-${goal.id}`}
                checked={goal.selected}
                onCheckedChange={() => toggleGoalSelection(goalType, goal.id)}
              />
              <label
                htmlFor={`${goalType}-${goal.id}`}
                className="ml-2 text-sm font-medium"
              >
                {goal.label}
              </label>
            </div>
            
            {goal.selected && (
              <div className="mt-3 pl-6">
                {goal.custom && (
                  <div className="mb-2">
                    <Input
                      placeholder="Please specify"
                      value={goal.customValue}
                      className="bg-[#9E7C47]/5 border-[#9E7C47]/30 text-white text-sm"
                      onChange={(e) => updateCustomGoalValue(goalType, e.target.value)}
                    />
                  </div>
                )}
                
                <div>
                  <Label className="text-xs text-white/70 mb-1 block">Priority Level</Label>
                  <ToggleGroup 
                    type="single" 
                    value={String(goal.priority)} 
                    onValueChange={(value) => updateGoalPriority(goalType, goal.id, parseInt(value))}
                    className="justify-start"
                  >
                    <ToggleGroupItem value="1" size="sm" className="text-xs">Low</ToggleGroupItem>
                    <ToggleGroupItem value="2" size="sm" className="text-xs">Medium</ToggleGroupItem>
                    <ToggleGroupItem value="3" size="sm" className="text-xs">High</ToggleGroupItem>
                  </ToggleGroup>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
  
  return (
    <div>
      <h2 className="text-xl font-semibold mb-6">Financial Goals Assessment</h2>
      
      {renderGoalsList(
        shortTermGoals, 
        'short', 
        'Short-term Goals (1-3 years)', 
        'Select goals you want to achieve in the next 1-3 years.'
      )}
      
      {renderGoalsList(
        mediumTermGoals, 
        'medium', 
        'Medium-term Goals (3-7 years)', 
        'Select goals you want to achieve in the next 3-7 years.'
      )}
      
      {renderGoalsList(
        longTermGoals, 
        'long', 
        'Long-term Goals (7+ years)', 
        'Select goals you want to achieve in 7+ years.'
      )}
    </div>
  );
};

export default ProfileFinancialGoals;
